<script>
    import filter from 'lodash/filter'
    import concat from 'lodash/concat'
    import isEmpty from 'lodash/isEmpty';
    import { Link } from 'svelte-routing'
    import { onMount } from 'svelte'
    import SvelteSelect from 'svelte-select'
    import Icon from 'fa-svelte'
    import { faSpinner } from '@fortawesome/free-solid-svg-icons/faSpinner'
    import { faCheck } from '@fortawesome/free-solid-svg-icons/faCheck'

    import { hiringApi } from 'src/api'
    import Btn from 'src/fields/Button.svelte'
    import Select from 'src/fields/Select.svelte'
    import Text from 'src/fields/Text.svelte'
    import TextArea from 'src/fields/TextArea.svelte'
    import File from 'src/fields/File.svelte'
    import MultipleSelect from 'src/fields/MultipleSelect.svelte'
    import RepeatableGroup from 'src/fields/RepeatableGroup.svelte'
    import Date from 'src/fields/Date.svelte'

    export let id

    onMount(async () => {
        await fetchJob()
    })

    const [FIELD_OPTIONAL, FIELD_REQUIRED, FIELD_PROMPT] = [0, 1, 2]

    let job = {}
    let error = false
    let serverErrors = {}
    let warning = false
    let loading = false
    let finished = false
    let formData = {}
    let candidate = {}
    let apiReadyCandidate = {}
    let requiredFields = []
    let promptingFields = []
    let missingFields = []
    let warningFields = []

    const FIELD_MAP = {
        'Text': Text,
        'Text Area': TextArea,
        'Single choice': Select,
        'Multiple choice': MultipleSelect,
        'File': File,
        'Repeatable group': RepeatableGroup,
        'Date': Date,
    }
    const CANDIDATE_FIELDS = [
        { title: 'First Name', key: 'first_name' },
        { title: 'Last Name', key: 'last_name' },
        { title: 'E-mail', key: 'email' },
        { title: 'Phone', key: 'phone' },
    ]
    const fetchJob = async () => {
        const { data } = await hiringApi.get(`/public/job/${id}/`)
        job = data
    }

    const submit = async (force) => {
        error = missingFields.length > 0
        warning = warningFields.length > 0
        if (error || (warning && !force)) {
            return
        }
        try {
            serverErrors = {}
            loading = true

            await hiringApi.post('/public/candidate/', apiReadyCandidate, {
                headers: { 'Content-Type': 'application/json' }
            })
            finished = true
        } catch(e) {
            if (e.response && e.response.data) serverErrors = e.response.data
        } finally {
            loading = false
        }
    }
    $: apiReadyCandidate = {
        ...candidate,
        job: job.id,
        locations: candidate.locations ? candidate.locations.map(({ value }) => value) : [],
        form_data: formData,
    };

    $: activeJobFields = filter(job.form_structure, field => (field.active !== false));
    $: requiredFields = filter(activeJobFields, { requirement: FIELD_REQUIRED });
    $: promptingFields = filter(activeJobFields, { requirement: FIELD_PROMPT });

    $: missingFields = concat(
            filter(requiredFields, field => !formData[field.key]),
            filter(CANDIDATE_FIELDS, field => !candidate[field.key])
    );
    $: warningFields = filter(promptingFields, field => !formData[field.key])
    $: locationItems = (job && job.locations)
            ? job.locations.map(location => ({ value: location.id, label: location.title }))
            : []
</script>

<main>
    {#if Object.values(job).length}
        <h1 class="job-title">{job.title}</h1>
        <span class="company-name">
            at Boutique Air
            <Link to="jobs/">
                <span class="link">(View all jobs)</span>
            </Link>
        </span>

        <div class="job-post">
            {@html job.rich_text}
        </div>
        {#if job.form_structure}
            <div class="application">
                <h1>Apply to this job</h1>

                {#if finished}
                    <div>
                        <span class="success-icon"><Icon icon={faCheck}/></span>&nbsp;
                        <span>Thank you! Your application has been submitter successfully.</span>
                    </div>
                {:else}
                    <div class="application-split">
                        <div class="application-field" style="margin-right: 20px;">
                            <label class="application-field__label">First name</label>
                            <Text bind:value={candidate.first_name}/>
                        </div>
                        <div class="application-field">
                            <label class="application-field__label">Last name</label>
                            <Text bind:value={candidate.last_name}/>
                        </div>
                    </div>
                    <div class="application-split">
                        <div class="application-field" style="margin-right: 20px;">
                            <label class="application-field__label">Email</label>
                            <Text bind:value={candidate.email}/>
                        </div>
                        <div class="application-field">
                            <label class="application-field__label">Phone</label>
                            <Text bind:value={candidate.phone}/>
                        </div>
                    </div>

                    {#if job.locations.length}
                        <div class="application-field">
                            <label class="application-field__label">Locations</label>
                            <SvelteSelect items={locationItems} isMulti={true}
                                          bind:selectedValue={candidate.locations} />
                        </div>
                    {/if}

                    <hr style="margin: 40px 0;"/>

                    {#each activeJobFields as field}
                        <div class="application-field">
                            <label class="application-field__label">{field.title}</label>
                            <svelte:component
                                this={FIELD_MAP[field.type]}
                                field={field}
                                bind:value={formData[field.key]}
                            />
                        </div>
                    {/each}

                    {#if !isEmpty(serverErrors)}
                        <div class="application__errors">
                            <ul class="error-list">
                                {#each Object.values(serverErrors) as error}
                                    <li class="error-list__item">{error}</li>
                                {/each}
                            </ul>
                        </div>
                    {/if}
                    {#if error && missingFields.length}
                        <div class="application__errors">
                            Following fields are required:
                            <ul class="error-list">
                                {#each missingFields as field}
                                    <li class="error-list__item">{field.title}</li>
                                {/each}
                            </ul>
                        </div>
                    {:else if warning && promptingFields.length}
                        <div class="application__errors">
                            Are you sure you want to leave this fields empty?
                            <ul class="error-list">
                                {#each promptingFields as field}
                                    <li class="error-list__item">{field.title}</li>
                                {/each}
                            </ul>
                            <Btn on:click={() => submit(true)}>Yes, submit my application</Btn>
                        </div>
                    {/if}

                    <div class="btn-wrapper">
                        <Btn on:click={() => submit(false)}>
                            <div class="submit-inner">
                                {#if loading && !finished}
                                    <span class="spinner"><Icon icon={faSpinner}/></span>&nbsp;&nbsp;
                                {/if}
                                <span>Submit</span>
                            </div>
                        </Btn>
                    </div>
                {/if}
            </div>
        {/if}
    {/if}
</main>

<style>
    main {
        font-size: 13px;
        line-height: 20px;
    }

    main :global(hr) {
        background-color: #DADADA;
        height: 1px;
        border: none;
    }

    main :global(a) {
        color: #2975CA;
    }

    .job-title {
        margin-bottom: 10px;
        line-height: 1.1;
    }

    .company-name {
        font-size: 16px;
    }

    .link {
        font-size: 13px;
        color: #2975CA;
    }


    .application {
        padding: 15px 30px 30px 30px;
        background-color: #f3f3f3;
        border-radius: 5px;
        margin: 20px 0;
    }

    .application-split {
        display: flex;
        flex-flow: row nowrap;
    }


    .application-field {
        margin-bottom: 10px;
        flex: 1 1 50%;
    }

    .application-field__label {
        color: #7b7b7b;
        margin-bottom: 3px;
    }

    .application-split .application-field {
        margin-right: 5px;
    }

    .application h1 {
        font-size: 18px;
        margin-bottom: 15px;
    }

    .btn-wrapper {
        display: flex;
        justify-content: flex-end;
        border-top: 1px solid rgba(0, 0, 0, .05);
        margin-top: 30px;
        padding-top: 20px;
    }

    .application__errors {
        background: #ffc8cf;
        color: #6f0a14;
        padding: 10px;
        border-radius: 8px;
    }

    .submit-inner {
        display: flex;
        align-items: center;
    }

    .spinner {
        display: flex;
        height: 14px;
        width: 14px;
        animation: spinner 1.2s linear infinite;
    }

    @keyframes spinner {
        0% {
            transform: rotate(0deg);
        }
        100% {
            transform: rotate(359deg);
        }
    }

    .success-icon {
        color: #6CAE75;
    }
</style>
